import React from "react";
import { useTranslation } from "react-i18next";
import { HiX } from "react-icons/hi";
import { CustomButton } from "../../../atoms/CustomButton";
import Spinner from "../../../atoms/Spinner";
import NoTranslate from "../../../atoms/NoTranslate";
import type { MemberSettings } from "../../../../hooks/agenda";
import AvailabilityText from "./AvailabilityText";
import ProfileLink from "./ProfileLink";

export interface TypePerson {
  user_identification: string;
  name: string | null;
  public_name: string | null;
}

export type PersonAvailability =
  | { state: "hidden" }
  | { state: "loading" }
  | { state: "failed" }
  | { state: "known"; settings: MemberSettings | null };

interface TypeMembersFieldProps {
  people: TypePerson[];
  availabilityOf: (userId: string) => PersonAvailability;
  profilePathOf: (userId: string) => string | null;
  onRemove?: (userId: string) => void;
}

const K = "agenda.types.drawer";

const TypeMembersField: React.FC<TypeMembersFieldProps> = ({
  people,
  availabilityOf,
  profilePathOf,
  onRemove,
}) => {
  const { t } = useTranslation();

  if (people.length === 0) {
    return (
      <p className="mt-2 rounded-md border border-gray-200 px-3 py-2 text-sm text-gray-600">
        {t(`${K}.membersEmpty`)}
      </p>
    );
  }

  return (
    <ul className="mt-2 divide-y divide-gray-100 rounded-md border border-gray-200">
      {people.map((person) => {
        const id = person.user_identification;
        const name = person.name || (t(`${K}.unnamedMember`) as string);
        const availability = availabilityOf(id);
        const profilePath = profilePathOf(id);
        return (
          <li
            key={id}
            className="flex flex-col gap-2 px-3 py-3 sm:flex-row sm:items-start sm:gap-3"
          >
            <div className="min-w-0 flex-1 space-y-1">
              <p className="truncate text-sm font-medium text-gray-900">
                <NoTranslate>{name}</NoTranslate>
              </p>
              <dl className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-3 gap-y-1 text-xs">
                <dt className="text-gray-500">{t(`${K}.publicNameLabel`)}</dt>
                <dd className="min-w-0 break-words text-gray-900">
                  {person.public_name ? (
                    <NoTranslate>{person.public_name}</NoTranslate>
                  ) : (
                    <span className="text-amber-700">
                      {t(`${K}.unnamedMember`)}
                    </span>
                  )}
                </dd>
                {availability.state !== "hidden" && (
                  <>
                    <dt className="text-gray-500">
                      {t(`${K}.availabilityLabel`)}
                    </dt>
                    <dd className="min-w-0 break-words text-gray-900">
                      {availability.state === "loading" && (
                        <Spinner size="sm" />
                      )}
                      {availability.state === "failed" && (
                        <span className="text-red-600">
                          {t(`${K}.availabilityError`)}
                        </span>
                      )}
                      {availability.state === "known" && (
                        <AvailabilityText settings={availability.settings} />
                      )}
                    </dd>
                  </>
                )}
              </dl>
            </div>
            {(profilePath || onRemove) && (
              <div className="flex shrink-0 flex-wrap items-center gap-2">
                {profilePath && <ProfileLink href={profilePath} name={name} />}
                {onRemove && (
                  <CustomButton
                    variant="none"
                    size="icon"
                    className="rounded-md text-gray-400 enabled:hover:text-red-600"
                    icon={<HiX size={16} />}
                    aria-label={t(`${K}.removeMemberOf`, { name }) as string}
                    onClick={() => onRemove(id)}
                  />
                )}
              </div>
            )}
          </li>
        );
      })}
    </ul>
  );
};

export default TypeMembersField;
